import React, { useState, useRef, useEffect, useContext } from "react";
import {
  Search,
  FileText,
  Database,
  Clock,
  AlertTriangle,
  HelpCircle,
  X,
} from "lucide-react";
import GlobalAuthenticatedContext from "../components/common/contexts/GlobalAuthenticatedContexts";

interface HelpTopic {
  id: string;
  name: string;
  icon: string;
  selected: boolean;
}

interface HelpContent {
  id: string;
  title: string;
  content: string;
  image?: string; // Optional image path
  topicId?: string;
}

interface HelpContentMap {
  [key: string]: HelpContent[];
}

interface HelpSupportProps {
  setCurrentPage?: (page: string) => void;
}

const HelpSupport: React.FC<HelpSupportProps> = ({ setCurrentPage }) => {

  
  const {globalAuthenticated} = useContext(
    GlobalAuthenticatedContext
  );

  const userRoles = globalAuthenticated?.userRoles || [];
  console.log("user roles, use=", userRoles)
  // Mock initial data
  const initialTopics: HelpTopic[] = [
    {
      id: "getting-started",
      name: "Getting Started",
      icon: "FileText",
      selected: true,
    },
    {
      id: "sharepoint-config",
      name: "SharePoint Configuration",
      icon: "Database",
      selected: false,
    },
    {
      id: "knowledge-base",
      name: "Knowledge Base",
      icon: "FileText",
      selected: false,
    },
    {
      id: "trigger-config",
      name: "Trigger Configuration",
      icon: "Clock",
      selected: false,
    },
    {
      id: "troubleshooting",
      name: "Troubleshooting",
      icon: "AlertTriangle",
      selected: false,
    },
    {
      id: "faq",
      name: "FAQ",
      icon: "HelpCircle",
      selected: false,
    },
  ];

  const initialContent: HelpContentMap = {
    "getting-started": [
      {
        id: "initial-setup",
        title: "Initial Setup Process",
        content:
          'The Initial Setup process is a one-time configuration required when first installing the SharePoint Connector. It sets up the necessary OAuth credentials, roles, and permissions in Genesys Cloud to enable system-to-system access. Follow these steps to complete the setup: \n1. Navigate to the Initial Setup page.\n2. Follow the step-by-step wizard to configure the following:\n- OAuth Client: Create an OAuth client for system-to-system access. The wizard will automatically generate a client named "SharePoint KB Connector" with Client Credentials grant type and assign the primary role "KBC Installation Wizard Admin".\n- Roles: Assign roles to manage permissions. The wizard assigns "SharePoint Connector Admin" and "SharePoint Connector View" roles to manage and view connector activities.\n   - Scopes: Define permission scopes for backend access. The wizard grants scopes such as audits, conversations, integrations, knowledge, oauth, organization-authorization, user-basic-info, and users.\n3. Once the setup is complete, you will see a "Setup Successfully Completed" message with the configured details.\n4. Click the "Go to SharePoint Config" button to proceed with configuring SharePoint connections.',
        image:"setup-required.png"
        },
      {
        id: "understanding-setup-details",
        title: "Understanding Initial Setup Details",
        content:
          'After completing the Initial Setup, the page displays the following details:\n- OAuth Client: Shows the client name (e.g., SharePoint KB Connector), grant type (Client Credentials), and primary role (KBC Installation Wizard Admin). You can click "View" to see more details like Client ID and Secret.\n- Roles: Lists the assigned roles (e.g., SharePoint Connector Admin, SharePoint Connector View) for managing permissions. Click "View" to see role details.\n- Scopes: Displays the granted permission scopes (e.g., audits, knowledge, oauth, etc.) required for backend access. Click "View" to see the full list of scopes.\n These settings ensure the connector has the necessary permissions to interact with Genesys Cloud and SharePoint.',
        image:"setup-completed.png"
      },
      {
        id: "first-kb",
        title: "Creating Your First Knowledge Base",
        content:
          'Once the initial setup is complete, you can create your first knowledge base and map it to SharePoint content. Navigate to the Knowledge Base Management page and click "Add Knowledge Base". Follow the wizard to configure your knowledge base name, description, and select SharePoint content to include.',
      },
      {
        id: "dashboard",
        title: "Understanding the Dashboard",
        content:
          "The dashboard provides an overview of your integration status, knowledge bases, and recent activity. You can see sync status, take quick actions, and monitor the health of your integration from this central location.",
        image:"dashboard.png"
        },
    ],
    "sharepoint-config": [
      {
        id: "overview",
        title: "Overview of SharePoint Configuration",
        content:
          "The SharePoint Configuration section allows you to manage connections to SharePoint sites. You can add new sites, edit existing configurations, validate connections, and delete sites as needed. Each configuration includes the site name, URL, associated knowledge bases, connection status, supported file types, and the last validation date.",
      },
      {
        id: "adding-site",
        title: "Adding a New SharePoint Site",
        content:
          'To add a new SharePoint site, click the "Add Site" button. You will need to provide the following details:\n- Site Name: A unique name for the SharePoint site (e.g., Tech Site).\n- Site URL: The full URL of the SharePoint site (e.g., https://genesyspsdev.sharepoint.com/sites/techsite).\n- File Inclusions: Select the file types to include (e.g., doc, txt).\n After entering the details, you can test the connection by validating it before saving.',
        image:"add-site.png"
      },
      {
        id: "validating-connection",
        title: "Validating a SharePoint Connection",
        content:
          'Before saving a new or edited configuration, you can validate the connection to ensure it is working. Click the validate button (checkmark icon) next to the site in the list. The status will update to "Connected" if successful, and the "Last Validated" column will show the date and time of the validation (e.g., 2025-05-28 21:14).',
        image:"validate-site.png"
        },
      {
        id: "editing-site",
        title: "Editing a SharePoint Site Configuration",
        content:
          "To edit an existing SharePoint site configuration, click the edit button (pencil icon) next to the site. You can modify the site name, URL, or file inclusions. After making changes, validate the connection again to ensure it still works, then save the updated configuration.",
        image:"edit-site.png"
        },
      {
        id: "deleting-site",
        title: "Deleting a SharePoint Site Configuration",
        content:
          "To delete a SharePoint site configuration, click the delete button (trash icon) next to the site. This will remove the site from the list and disconnect any associated knowledge bases. Be cautious, as this action cannot be undone.",
        image:"delete-site-action.png"
        },
      {
        id: "understanding-columns",
        title: "Understanding the SharePoint Sites Table",
        content:
          "The SharePoint Sites table displays the following columns:\n- Site Name: The name of the SharePoint site (e.g., Tech Site).\n- Site URL: The URL of the SharePoint site.\n- Knowledge Bases: The number of knowledge bases linked to the site (e.g., 0 or 1).\n- Status: The connection status (e.g., Connected).\n- File Types: The included file types (e.g., doc, txt).\n- Last Validated: The date and time of the last successful validation.\n- Actions: Options to edit, validate, or delete the configuration.",
        image:"list-site.png"
        },
    ],
    "knowledge-base": [
      {
        id: "overview",
        title: "Overview of Knowledge Base Management",
        content:
          "The Knowledge Base Management section allows you to create, edit, sync, and delete knowledge bases. Each knowledge base is linked to SharePoint content and can be managed through a simple interface showing the name, description, number of articles, last sync date, status, and available actions.",
      },
      {
        id: "creating-kb",
        title: "Creating a Knowledge Base",
        content:
          'To create a new knowledge base, click the "Add Knowledge Base" button. Provide the following details:\n- Name: A unique name for the knowledge base (e.g., Technical Documentation).\n- Description: A brief description of the knowledge base (e.g., Technical Documentation Description).\n- Language: Choose applicable language code from dropdown. \n- Content Serach: Enable if you want content search active once Knowledge base is created, disabled by defalult.\n- SharePoint Content: Select the SharePoint site and specific content to include. \n Once created, the knowledge base will appear in the list with an initial status of "Active".',
        image:"add-kb.png"
        },
      {
        id: "editing-kb",
        title: "Editing a Knowledge Base",
        content:
          "To edit an existing knowledge base, click the edit button (pencil icon) next to the knowledge base in the list. You can update the name, description, or linked SharePoint content. After making changes, save the updated knowledge base to apply the modifications.",
        image:"edit-kb-action.png"
        },
      {
        id: "syncing-kb",
        title: "Syncing a Knowledge Base",
        content:
        'To sync a knowledge base with its linked SharePoint content, click the sync button (refresh icon) next to the knowledge base. This will update the articles in the knowledge base based on the latest SharePoint content. The "Last Sync" column will update with the date and time of the sync (e.g., 2025-05-28 19:38).',
      image:"sync-kb-action.png"
      },
      {
        id: "deleting-kb",
        title: "Deleting a Knowledge Base",
        content:
          "To delete a knowledge base, click the delete button (trash icon) next to the knowledge base. This will permanently remove the knowledge base and its associated articles. Be cautious, as this action cannot be undone.",
        image:"delete-kb-action.png"
        },
      {
        id: "understanding-columns",
        title: "Understanding the Knowledge Bases Table",
        content:
          "The Knowledge Bases table displays the following columns:\n- Name: The name of the knowledge base (e.g., Technical Documentation).\n- Description: A brief description of the knowledge base.\n- Articles: The number of articles in the knowledge base (e.g., 2).\n- Last Sync: The date and time of the last sync with SharePoint content (e.g., 2025-05-28 19:38).\n- Status: The current status of the knowledge base (e.g., Active).\n- Actions: Options to edit, sync, or delete the knowledge base.",
        image:"list-kb.png"
        },
    ],
    "trigger-config": [
      {
        id: "overview",
        title: "Overview of Trigger Configuration",
        content:
          "The Trigger Configuration section allows you to set up synchronization triggers for your knowledge bases. You can choose between two types of triggers: Ad-hoc Update, which lets you manually trigger a sync whenever needed, and Scheduled Batch Process (CRON), which automates syncs on a predefined schedule.",
      },
      {
        id: "selecting-trigger-type",
        title: "Selecting a Trigger Type",
        content:
          "On the Trigger Configuration page, you can select the trigger type:\n- Ad-hoc Update: Manually initiate a sync at any time. This is useful for immediate updates when new content is added to SharePoint.\n- Scheduled Batch Process (CRON): Set up an automated sync schedule using CRON expressions. This is ideal for regular, hands-off synchronization of knowledge bases.",
      },
      {
        id: "configuring-adhoc-sync",
        title: "Configuring an Ad-hoc Update",
        content:
          'To configure an ad-hoc sync:\n1. Navigate to the Trigger Configuration page.\n2. Select the "Ad-hoc Update" option under Trigger Type.\n3. Click the "Trigger Manual Sync Now" button to initiate the sync immediately.\n No automatic scheduling will be set, and the sync will start right away, updating all connected knowledge bases with the latest SharePoint content.',
        image: "adhoc-trigger.png"
        },
      {
        id: "configuring-scheduled-sync",
        title: "Configuring a Scheduled Batch Process (CRON)",
        content:
          'To configure a scheduled sync:\n1. Navigate to the Trigger Configuration page.\n2. Select the "Scheduled Batch Process (CRON)" option under Trigger Type.\n3. Enter a valid CRON expression to define the schedule (e.g., "0 0 8 * * ?" for daily at 08:00 AM IST).\n4. Save the configuration.\n The sync will run automatically at the specified intervals, keeping your knowledge bases up-to-date.',
         image: "schedule-trigger.png"
        },
    ],
    troubleshooting: [
      {
        id: "common-limit-1",
        title:
          "Why some users are not able to view menu to configure SharePoint connections?",
        content:
          "Configure SharePoint connection menu is only avialable with users having sharepoint-admin role. Users with sharepoint-view role cannot view this menu in KBC application.",
      },
      {
        id: "common-limit-2",
        title:
          "Why for some paths system only pulls partial sharepoint resources in SharePoint Browser?",
        content:
          "If a SharePoint Path contains more than 4k items, system will pull first 4k items in a single iteration and enables a Fetch more pages button with double right arrow icon in pagination section, which can be leverage to pull out next set of 4k items and so on.",
      },
      {
        id: "common-limit-3",
        title: "Sharepoint configuration delete action button is disabled.",
        content:
          "If there is any active Knowledge Base tagged to a SharePoint configuration, delete operation for the sharepoint configuration is prohibited. To delete it, first delete the knowledge base tagged to that sharepoint configuration.",
      },
      {
        id: "common-limit-4",
        title:
          "Why even after configuring SharePoint ouath token, system is not pulling sharepoint resources in Browser?",
        content:
          "Check if aouth token that you have configured has necessary permissions to read and list sharepoint resources by logging in MS Intra Portal.",
      },
      {
        id: "common-limit-5",
        title: "How to troubleshoot an issue not listed here.",
        content:
          "If you are experencing an issue not listed here. Kindly drop an email to GC support team.",
      },
    ],
    faq: [
      {
        id: "faq-1",
        title: "How often does the connector sync content?",
        content:
          "The connector syncs content based on the trigger configuration you set up. It can be scheduled (daily, weekly, etc.), triggered manually, or set up to respond to SharePoint webhooks in real-time.",
      },
      {
        id: "faq-2",
        title: "What SharePoint content types are supported?",
        content:
          "The connector supports various SharePoint content types including documents and libraries. It can extract content from Word documents, PDFs, text files and html files.",
      },
      {
        id: "faq-3",
        title: "What is the maximum number of files supported in a single KB?",
        content:
          "The maximum number of files supported in a single Knowledge Base is 15,000 files.",
      },
      {
        id: "faq-4",
        title:
          "What is the maximum size of article content (uploaded file size)?",
        content:
          "The maximum size of article content for uploaded files is 300 KB.",
      },
      {
        id: "faq-5",
        title:
          "What is the maximum number of Knowledge Base within an organistation in Genesys Cloud?",
        content:
          "The maximum number of Knowledge Base within an organistation is 500 in Genesys cloud.",
      },
      {
        id: "faq-6",
        title: "What is the maximum number of variations for one article?",
        content:
          "The maximum number of variations for single article supported is 10.",
      },
      {
        id: "faq-7",
        title:
          "How to Set Up OAuth Client ID and Secret in Microsoft Entra Portal?",
        content: `Below is a step wise guide that walks you through registering an application in Microsoft Entra (formerly Azure Active Directory) to obtain OAuth credentials for authentication.
        Prerequisites\n- Microsoft Entra (Azure AD) tenant access\n- Appropriate permissions to register applications (Application Developer role or higher)\n- Admin consent may be required for certain permissions if a Non-admin account is used.\nStep-by-Step Instructions 
            \n1. Access Microsoft Entra Portal - Navigate to the Microsoft Entra admin center \n- Sign in with your organizational account \n- Ensure you're in the correct tenant/directory 
            \n2. Navigate to App Registrations\n-  In the left navigation panel, expand Identity\n-  Click on Applications\n-  Select App registrations\n-  Click + New registration 
            \n3. Configure Basic App Information\n-  Name: Enter a meaningful name for your application\n-  Supported account types: Choose one of the following:\n- Accounts in any organizational directory - Multi-tenant\n- Accounts in any organizational directory and personal Microsoft accounts - Multi-tenant + personal accounts\n- Redirect URI (optional for now):\n- Select platform type (Web, Public client, etc.)\n- Enter your application's redirect/callback URL\n- You can add more redirect URIs later\n - Click Register
            \n4. Obtain Your Client ID\n- After registration, you'll be taken to your app's overview page:\n- Application (client) ID is displayed prominently - copy and save this value\n- Directory (tenant) ID is also shown - save this if needed for your application
            \n5. Create a Client Secret\n-	In the left navigation of your app registration, click Certificates & secrets\n-	Under Client secrets section, click + New client secret\n-	Description: Enter a meaningful description (e.g., "Production API Secret")\n- Expires: Choose applicable expiration period and click add\n- Important: Copy the secret Value immediately - it will only be shown once\n- Also note the secret's Secret ID for reference
            \n6. Configure API Permissions (if needed)\n- Click API permissions in the left navigation\n- Click + Add a permission\n- Choose the API you need access to: \n- Microsoft Graph (most common)\n- Select Application permissions as appropriate(For KBC app, Select Sites.Selected & File.ReadWriteAll permissions)\n- Click Add permissions\n- If required, click Grant admin consent (admin role required)
            \n7. Configure Authentication Settings\n- Click Authentication in the left navigation.\n- Add redirect URIs if not done during registration: \n-	Click + Add a platform\n-	Choose platform type (Web, Single-page application, etc.)\n-	Enter redirect URI(optional)\n- Configure additional settings as needed: \n- Front-channel logout URL\n- Implicit grant and hybrid flows (if using older OAuth flows)\n- Advanced settings for specific scenarios.
            \n8. Optional: Configure Token Configuration\n- Click Token configuration to customize claims in tokens\n- Add optional claims if your application needs specific user information\n- Configure groups claims if needed`,
      },
    ],
  };

  // Component state
  const [topics, setTopics] = useState<HelpTopic[]>(initialTopics);
  const [content] = useState<HelpContentMap>(initialContent);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<HelpContent[]>([]);
  const [isSearchExpanded, setIsSearchExpanded] = useState<boolean>(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Function to format content with proper line breaks and bullet points
  const formatContent = (text: string) => {
    const lines = text.split("\n");
    const formattedLines = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Skip empty lines but add spacing
      if (line.trim() === "") {
        formattedLines.push(<br key={`br-${i}`} />);
        continue;
      }

      // Check if line starts with a number (numbered list)
      if (/^\d+\./.test(line.trim())) {
        formattedLines.push(
          <div key={i} className="ml-4 mb-2">
            <span className="font-medium text-blue-600">
              {line.match(/^\d+\./)?.[0]}
            </span>
            <span className="ml-2">{line.replace(/^\d+\.\s*/, "")}</span>
          </div>
        );
      }
      // Check if line starts with a dash or bullet point
      else if (/^\s*-/.test(line)) {
        const indent = line.search(/\S/); // Find first non-whitespace character
        const indentLevel = Math.floor(indent / 2); // Assume 2 spaces per indent level
        const content = line.replace(/^\s*-\s*/, "");

        formattedLines.push(
          <div
            key={i}
            className={`flex items-start mb-2 ${
              indentLevel > 0 ? `ml-7` : "ml-7"
            }`}
          >
            <span className="text-blue-500 mr-2 mt-1 flex-shrink-0">•</span>
            <span className="text-gray-700">{content}</span>
          </div>
        );
      }
      // Regular paragraph text
      else {
        formattedLines.push(
          <p key={i} className="mb-3 text-gray-700 leading-relaxed">
            {line}
          </p>
        );
      }
    }

    return <div className="space-y-1">{formattedLines}</div>;
  };

  // Handle URL hash and set current page on mount
  useEffect(() => {
    setCurrentPage?.("help"); // Set Sidebar to highlight "help" page
    const hash = window.location.hash.replace("#", "").toLowerCase();
    if (hash) {
      let targetTopicId: string | undefined;
      Object.keys(content).some((topicId) => {
        const found = content[topicId].find(
          (item) => item.id.toLowerCase() === hash
        );
        if (found) {
          targetTopicId = topicId;
          return true;
        }
        return false;
      });

      if (
        targetTopicId &&
        !topics.find((topic) => topic.id === targetTopicId && topic.selected)
      ) {
        const updatedTopics = topics.map((topic) => ({
          ...topic,
          selected: topic.id === targetTopicId,
        }));
        setTopics(updatedTopics);
      }

      // Scroll to the content section
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    // eslint-disable-next-line
  }, []);

  const handleTopicSelect = (topicId: string) => {
    // Update selected topic
    const updatedTopics = topics.map((topic) => ({
      ...topic,
      selected: topic.id === topicId,
    }));

    setTopics(updatedTopics);

    // Clear search when changing topics
    setSearchQuery("");
    setSearchResults([]);
    setIsSearchExpanded(false);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Expand search when user starts typing
    if (query.trim() !== "") {
      setIsSearchExpanded(true);
    } else {
      setIsSearchExpanded(false);
      setSearchResults([]);
      return;
    }

    // Simple search across all help content
    const results: HelpContent[] = [];

    Object.keys(content).forEach((topicId) => {
      content[topicId].forEach((item) => {
        if (
          item.title.toLowerCase().includes(query.toLowerCase()) ||
          item.content.toLowerCase().includes(query.toLowerCase())
        ) {
          results.push({
            ...item,
            topicId,
          });
        }
      });
    });

    setSearchResults(results);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setIsSearchExpanded(false);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const getSelectedTopic = (): HelpTopic | undefined => {
    return topics.find((topic) => topic.selected);
  };

  const getTopicIcon = (iconName: string) => {
    switch (iconName) {
      case "FileText":
        return <FileText size={20} />;
      case "Database":
        return <Database size={20} />;
      case "Clock":
        return <Clock size={20} />;
      case "AlertTriangle":
        return <AlertTriangle size={20} />;
      case "HelpCircle":
        return <HelpCircle size={20} />;
      default:
        return <FileText size={20} />;
    }
  };

  const renderContent = () => {
    // If there's a search query with results
    if (searchQuery.trim() !== "" && searchResults.length > 0) {
      return (
        <div className="p-6">
          <h2 className="text-lg font-medium mb-4">Search Results</h2>
          <p className="text-sm text-gray-500 mb-6">
            Found {searchResults.length} results for "{searchQuery}"
          </p>
          <div className="divide-y divide-gray-200">
            {searchResults.map((item) => (
              <div key={item.id} className="py-6">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <span className="mr-2">
                    {getTopicIcon(
                      topics.find((t) => t.id === item.topicId)?.icon ||
                        "FileText"
                    )}
                  </span>
                  <span>
                    {topics.find((t) => t.id === item.topicId)?.name || "Topic"}
                  </span>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  {item.title}
                </h3>
                <div className="text-gray-600">
                  {formatContent(item.content)}
                </div>
                {item.image && (
                <div className="mt-4">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-3/4 h-auto rounded-lg shadow-sm"
                  />
                </div>
              )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // If there's a search query but no results
    if (searchQuery.trim() !== "" && searchResults.length === 0) {
      return (
        <div className="p-6 text-center">
          <AlertTriangle size={48} className="mx-auto text-yellow-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">
            No results found
          </h3>
          <p className="mt-1 text-gray-500">
            No help content matches your search query. Try using different
            keywords.
          </p>
        </div>
      );
    }

    // Otherwise, show the selected topic content
    const selectedTopic = getSelectedTopic();

    if (!selectedTopic) {
      return (
        <div className="p-6">
          <h2 className="text-lg font-medium mb-4">Getting Started</h2>
          <p className="text-gray-600">
            Please select a topic from the left menu to view help content.
          </p>
        </div>
      );
    }

    const topicContent = content[selectedTopic.id] || [];

    if (topicContent.length === 0) {
      return (
        <div className="p-6">
          <h2 className="text-lg font-medium mb-4">{selectedTopic.name}</h2>
          <div className="text-center py-8">
            <AlertTriangle size={48} className="mx-auto text-yellow-400 mb-4" />
            <p className="text-gray-600">
              There is currently no help content available for this topic.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="p-6">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900">
          {selectedTopic.name}
        </h2>
        <div className="space-y-10">
          {topicContent.map((item) => (
            <div
              key={item.id}
              id={item.id}
              className="border-b border-gray-100 pb-8 last:border-b-0"
            >
              <h3 className="text-xl font-medium text-gray-900 mb-4">
                {item.title}
              </h3>
              <div className="text-gray-700">{formatContent(item.content)}</div>
              {item.image && (
                <div className="mt-4">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-3/4 h-auto rounded-lg shadow-sm"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        
      </div>
    );
  };

  if(userRoles.length > 0 ) {
    return (
      <div className="pt-16 ml-64">
        <div className="p-6">
          {/* Header with search only on right */}
          <div className="flex justify-end items-center mb-6">
            <div className={`relative ${isSearchExpanded ? 'w-96' : 'w-64'} transition-all duration-300 ease-in-out`}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-gray-400" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="Search for help..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              {searchQuery && (
                <button
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={clearSearch}
                >
                  <X size={16} className="text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
          </div>

          <div className="flex gap-6">
            {/* Left Sidebar - Help Topics */}
            <div className="w-72 shrink-0">
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h3 className="text-base font-medium">Help Topics</h3>
                </div>

                <nav className="h-full overflow-y-auto" aria-label="Help Topics">
                  <ul className="py-2">
                    {topics.map((topic) => (
                      <li key={topic.id}>
                        <button
                          onClick={() => handleTopicSelect(topic.id)}
                          className={`w-full flex items-center px-4 py-2 text-sm font-medium ${
                            topic.selected
                              ? 'bg-blue-100 text-blue-900'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          <span className="mr-3 text-gray-500">{getTopicIcon(topic.icon)}</span>
                          <span className="truncate">{topic.name}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </div>

            {/* Right Content - Help Content or Search Results */}
            <div className="flex-1">
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                {renderContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (

    <div className="flex gap-6">
      
      {/* Left Sidebar - Help Topics */}
      <div className="w-72 shrink-0 bg-slate-800 text-white flex flex-col">
        <div className="px-4 py-3 border-b border-slate-700">
          <h3 className="text-lg font-semibold">KBC Connector Help Topics</h3>
        </div>
        <nav className="flex-1 overflow-y-auto" aria-label="Help Topics">
          <ul className="py-1">
            {topics.map((topic) => (
              <li key={topic.id}>
                <button
                  onClick={() => handleTopicSelect(topic.id)}
                  className={`w-full flex items-center px-4 py-2 text-sm font-medium text-left ${
                    topic.selected
                      ? "bg-slate-700 text-white"
                      : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  }`}
                >
                  <span className="mr-3">{getTopicIcon(topic.icon)}</span>
                  <span className="truncate">{topic.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      {/* Right Content Area */}
      <div className="flex-1">
        
        {/* Header with search only on right */}
        <div className="flex justify-end items-center mt-4 mb-6 mr-4">
          <div className={`relative ${isSearchExpanded ? "w-96" : "w-64"} transition-all duration-300 ease-in-out`}>
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={16} className="text-gray-400" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="Search for help..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {searchQuery && (
              <button
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={clearSearch}
              >
                <X size={16} className="text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {renderContent()}
        </div>
      </div>
    </div>

  );
};

export default HelpSupport;
